import {
  IAdminPanelApiCall,
  IRestApiService,
  AdminPanelListUsersParams,
  AdminPanelUsersResponse,
  AdminPanelSubscriptionDetails,
  AdminPanelInvoicesResponse,
  AdminPanelCustomInvoiceResult,
  AdminPanelDiscountInput,
  AdminPanelReportJob,
  AdminPanelSubscriptionAnalytics,
  AdminPanelAtRiskAccount,
} from '../types';

class AdminPanelApiCall implements IAdminPanelApiCall {
  private restApiService: IRestApiService;

  constructor(restApiService: IRestApiService) {
    this.restApiService = restApiService;
  }

  getUsers(params: AdminPanelListUsersParams): Promise<AdminPanelUsersResponse> {
    const query = new URLSearchParams({
      page: String(params.page),
      size: String(params.size),
      sortBy: params.sortBy || 'nextPaymentDate',
      sortDir: params.sortDir || 'asc',
    });
    if (params.statuses?.length) {
      query.set('statuses', params.statuses.join(','));
    }
    if (params.search?.trim()) {
      query.set('search', params.search.trim());
    }
    return this.restApiService.get<AdminPanelUsersResponse>(`admin-panel/users?${query.toString()}`);
  }

  getStatuses(): Promise<string[]> {
    return this.restApiService.get<string[]>('admin-panel/statuses');
  }

  getSubscriptionDetails(ownerId: string): Promise<AdminPanelSubscriptionDetails> {
    return this.restApiService.get<AdminPanelSubscriptionDetails>(`admin-panel/owner/${ownerId}/subscription`);
  }

  updateSubscription(ownerId: string, data: Record<string, any>): Promise<AdminPanelSubscriptionDetails> {
    return this.restApiService.put<AdminPanelSubscriptionDetails>(`admin-panel/owner/${ownerId}/subscription`, data);
  }

  getInvoices(ownerId: string, page: number, size: number): Promise<AdminPanelInvoicesResponse> {
    return this.restApiService.get<AdminPanelInvoicesResponse>(
      `admin-panel/owner/${ownerId}/invoices?page=${page}&size=${size}`
    );
  }

  createCustomInvoice(ownerId: string, data: { amount: number; purpose: string }): Promise<AdminPanelCustomInvoiceResult> {
    return this.restApiService.post<AdminPanelCustomInvoiceResult>(`admin-panel/owner/${ownerId}/custom-invoice`, data);
  }

  addDiscount(ownerId: string, data: AdminPanelDiscountInput): Promise<AdminPanelSubscriptionDetails> {
    return this.restApiService.post<AdminPanelSubscriptionDetails>(`admin-panel/owner/${ownerId}/discount`, data);
  }

  removeDiscount(ownerId: string): Promise<AdminPanelSubscriptionDetails> {
    return this.restApiService.delete<AdminPanelSubscriptionDetails>(`admin-panel/owner/${ownerId}/discount`);
  }

  extendTrial(ownerId: string, days: number): Promise<AdminPanelSubscriptionDetails> {
    return this.restApiService.post<AdminPanelSubscriptionDetails>(`admin-panel/owner/${ownerId}/extend-trial`, { days });
  }

  cancelSubscription(ownerId: string): Promise<AdminPanelSubscriptionDetails> {
    return this.restApiService.post<AdminPanelSubscriptionDetails>(`admin-panel/owner/${ownerId}/cancel`);
  }

  resumeSubscription(ownerId: string): Promise<AdminPanelSubscriptionDetails> {
    return this.restApiService.post<AdminPanelSubscriptionDetails>(`admin-panel/owner/${ownerId}/resume`);
  }

  blockSubscription(ownerId: string): Promise<AdminPanelSubscriptionDetails> {
    return this.restApiService.post<AdminPanelSubscriptionDetails>(`admin-panel/owner/${ownerId}/block`);
  }

  startUniqueClientsReport(dateFrom: string, dateTo: string, statuses: string[]): Promise<AdminPanelReportJob> {
    const query = new URLSearchParams({ dateFrom, dateTo, statuses: statuses.join(',') });
    return this.restApiService.get<AdminPanelReportJob>(`admin-panel/reports/unique-clients?${query.toString()}`);
  }

  exportUsers(params: Omit<AdminPanelListUsersParams, 'page' | 'size'>): Promise<Blob> {
    const query = new URLSearchParams({
      sortBy: params.sortBy || 'nextPaymentDate',
      sortDir: params.sortDir || 'asc',
    });
    if (params.statuses?.length) {
      query.set('statuses', params.statuses.join(','));
    }
    if (params.search?.trim()) {
      query.set('search', params.search.trim());
    }
    return this.restApiService.doRequest<Blob>(`admin-panel/users/export?${query.toString()}`, 'GET', undefined, undefined, {
      responseType: 'blob',
    });
  }

  getSubscriptionAnalytics(monthsBack?: number): Promise<AdminPanelSubscriptionAnalytics> {
    const query = monthsBack ? `?monthsBack=${monthsBack}` : '';
    return this.restApiService.get<AdminPanelSubscriptionAnalytics>(`admin-panel/analytics/subscriptions${query}`);
  }

  getAtRiskAccounts(limit?: number): Promise<AdminPanelAtRiskAccount[]> {
    const query = limit ? `?limit=${limit}` : '';
    return this.restApiService.get<AdminPanelAtRiskAccount[]>(`admin-panel/analytics/at-risk${query}`);
  }
}

export default AdminPanelApiCall;
